// Arquivo: src/lib/format.ts  (colar o conteúdo INTEGRAL deste .txt no Code Mode)
// Formatadores canônicos do Dash Engenharia (pt-BR).
// Regras: datas dd/mm/aaaa SEM new Date(); moeda R$ com 2 casas; percentuais com vírgula;
// números sem zeros à direita extras; dinheiro trafega como string decimal ("1234.56").

export function toNumber(value: unknown): number {
  if (value === null || value === undefined) return 0
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (typeof value === 'object' && value !== null && '$numberDecimal' in (value as Record<string, unknown>)) {
    return toNumber((value as { $numberDecimal: string }).$numberDecimal)
  }
  const text = String(value).trim().replace(/\s|R\$/g, '')
  if (!text || text === '-') return 0
  const normalized = text.includes(',') && text.includes('.')
    ? text.replace(/\./g, '').replace(',', '.')
    : text.includes(',')
      ? text.replace(',', '.')
      : text
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : 0
}

export function cents(value: unknown): bigint {
  const n = toNumber(value)
  const negative = n < 0
  const unsigned = BigInt(Math.round(Math.abs(n) * 100))
  return negative ? -unsigned : unsigned
}

export function fromCents(value: bigint): string {
  const negative = value < 0n
  const unsigned = negative ? -value : value
  return `${negative ? '-' : ''}${unsigned / 100n}.${String(unsigned % 100n).padStart(2, '0')}`
}

export function sumCents(values: Array<unknown>): bigint {
  return values.reduce<bigint>((total, value) => total + cents(value), 0n)
}

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

export function moneyFmt(value: unknown): string {
  return brl.format(toNumber(value))
}

export function moneyCompact(value: unknown): string {
  const n = toNumber(value)
  const abs = Math.abs(n)
  const sign = n < 0 ? '-' : ''
  if (abs >= 1e9) return `${sign}R$ ${(abs / 1e9).toFixed(2).replace('.', ',')} bi`
  if (abs >= 1e6) return `${sign}R$ ${(abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1).replace('.', ',')} mi`
  if (abs >= 1e3) return `${sign}R$ ${Math.round(abs / 1e3)} mil`
  return `${sign}${brl.format(abs)}`
}

// Data SEMPRE dd/mm/aaaa, sem conversão de fuso (usa os caracteres ISO).
export function dateBR(iso?: string | null): string {
  if (!iso) return '-'
  const head = String(iso).slice(0, 10)
  if (!head.includes('-')) return head
  const [y, m, d] = head.split('-')
  if (!y || !m || !d) return head
  return `${d}/${m}/${y}`
}

export function pctFmt(value: unknown, digits = 1): string {
  return `${toNumber(value).toFixed(digits).replace('.', ',')}%`
}

export function pctSigned(value: unknown, digits = 1): string {
  const n = toNumber(value)
  return `${n > 0 ? '+' : ''}${pctFmt(n, digits)}`
}

export const numFormatter = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 })

export function numFormat(value: unknown): string {
  return numFormatter.format(toNumber(value))
}

export function idcText(value: unknown): string {
  return toNumber(value).toFixed(2).replace('.', ',')
}

// "OBRA SOUL FONSECA - NITERÓI/RJ" -> "SOUL FONSECA"
export function shortObra(name?: string | null): string {
  if (!name) return '—'
  const withoutPrefix = String(name).replace(/^OBRA\s+/i, '')
  const cut = withoutPrefix.split(' - ')[0]
  return cut || withoutPrefix
}

export function trunc(text: unknown, size: number): string {
  const value = text === null || text === undefined || text === '' ? '—' : String(text)
  return value.length > size ? `${value.slice(0, size)}…` : value
}

export function orDash(text: unknown): string {
  const value = text === null || text === undefined || String(text).trim() === '' ? null : String(text).trim()
  return value ?? '—'
}
